import { Fragment, useEffect, useState, type SyntheticEvent } from 'react';
import { api, ApiHata, ROL_ETIKET, type KullaniciSatir, type Rol } from '../lib/api';
import { ROLES, rolEtiket } from '@mod/domain';
import { useOturum } from '../lib/oturum';

/** Rol listesi @mod/domain'den — şema, kural ve arayüz tek kaynaktan besleniyor. */
const ROLLER: readonly Rol[] = ROLES;
const TEMSIL_MODLARI = [
  { deger: 'tum_kullanicilar', etiket: 'Tüm kullanıcılar', not: 'Test dönemi' },
  { deger: 'sadece_test', etiket: 'Yalnız test hesapları', not: 'Pilot için önerilen' },
  { deger: 'kapali', etiket: 'Kapalı', not: 'Geçiş yok' },
] as const;

/**
 * @param kapaliGoster Kapatılmış hesaplar listelensin mi? Varsayılan HAYIR —
 *   kapalı hesap kadro listesini kalabalıklaştırır, kaydın kendisi durur.
 *   Anahtar Tanımlar ekranının tepesinde, üç listeyi birden yönetir.
 */
export function KullaniciYonetimi({ kapaliGoster = false }: { kapaliGoster?: boolean }) {
  const { ben } = useOturum();
  const [liste, setListe] = useState<KullaniciSatir[]>([]);
  const [hata, setHata] = useState<string | null>(null);
  const [temsilModu, setTemsilModu] = useState<string>('tum_kullanicilar');

  const yukle = async () => {
    try {
      setListe(await api.get<KullaniciSatir[]>('/kullanicilar'));
      const a = await api.get<{ temsilModu: string }>('/ayarlar');
      setTemsilModu(a.temsilModu);
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Yüklenemedi');
    }
  };

  useEffect(() => {
    void yukle();
  }, []);

  async function modDegistir(deger: string) {
    setTemsilModu(deger);
    await api.patch('/ayarlar', { temsilModu: deger }).catch(() => undefined);
  }

  const gorunur = kapaliGoster ? liste : liste.filter((k) => k.aktif);
  const testler = gorunur.filter((k) => k.testKullanicisi);
  const gercekler = gorunur.filter((k) => !k.testKullanicisi);
  const gizli = liste.length - gorunur.length;

  return (
    <div>
      <h2 className="mb-1 text-sm font-semibold text-navy">Çalışanlar ve kullanıcılar</h2>
      <p className="mb-4 text-sm text-wait">
        Sicil <span className="num">1–8999</span> gerçek saha kadrosu ·{' '}
        <span className="num">9000+</span> sistem ve test hesapları
      </p>

      {hata && (
        <p className="mb-4 rounded-(--radius-mod) bg-crit/10 p-3 text-sm text-crit">{hata}</p>
      )}

      <YeniKullanici onEklendi={yukle} />

      <Tablo baslik="Gerçek kullanıcılar" satirlar={gercekler} onDegisti={yukle} benId={ben?.id} adminMi={ben?.sistemRol === 'admin'} />
      {testler.length > 0 && (
        <Tablo baslik="Test hesapları" satirlar={testler} onDegisti={yukle} benId={ben?.id} adminMi={ben?.sistemRol === 'admin'} />
      )}
      {/*
        Kapalı hesap SİLİNMİŞ değildir; geçmiş işlerdeki adı, iz kaydındaki
        imzası ve çalışan kaydı yerinde durur. Gizlenen yalnız satırdır.
      */}
      {gizli > 0 && (
        <p className="-mt-4 mb-8 text-xs text-faint">
          {gizli} kapalı hesap gizli — yukarıdaki kutudan görebilirsiniz.
        </p>
      )}

      {ben?.sefYetkisi && <DikkatEsikAyari />}

      {ben?.sistemRol === 'admin' && <MailKilidiAyari />}

      {ben?.sistemRol === 'admin' && (
        <section className="mt-10 rounded-(--radius-mod) border border-line bg-surface shadow-(--shadow-mod) p-5">
          <h2 className="mb-1 font-semibold text-navy">Ayarlar — kullanıcı geçişi</h2>
          <p className="mb-4 text-sm text-wait">
            Admin kimlerin yerine geçebilir? Geçiş yapıldığında <strong>o kullanıcının
            yetkileriyle</strong> devam edilir; admin yetkisi geçici olarak düşer.
          </p>
          <div className="space-y-2">
            {TEMSIL_MODLARI.map((m) => (
              <label key={m.deger} className="flex items-center gap-3 text-sm">
                <input
                  type="radio"
                  name="temsilModu"
                  checked={temsilModu === m.deger}
                  onChange={() => void modDegistir(m.deger)}
                />
                <span>{m.etiket}</span>
                <span className="text-xs text-wait">({m.not})</span>
              </label>
            ))}
          </div>
          <p className="mt-4 border-t border-line/70 pt-3 text-xs leading-relaxed text-wait">
            Bu ayar <strong>kapsamı</strong> belirler, yetkiyi değil. Temsil altında yapılan her
            işlem her koşulda iz kaydına çift kimlikle yazılır ve bu kapatılamaz.
          </p>
        </section>
      )}
    </div>
  );
}

/** `POST /kullanicilar/telefon-sorgula` cevabı — formun hangi yoldan devam edeceği. */
type TelefonSorgu =
  | { durum: 'yeni' }
  | { durum: 'bu_santiyede'; ad: string; aktif: boolean }
  | { durum: 'baska_santiyede'; ad: string; hesapAktif: boolean };

/**
 * YENİ KULLANICI — önce telefon, sonra cevaba göre iki yol.
 *
 * ⚠️ İKİ ADIMLI (06.10.2026, müşteri bildirimi: *"başka şantiyeye kayıtlı
 *    personeli kendi şantiyeme ekleyemiyorum"*). Mevcut hesabı ekleme yolu
 *    01.09'dan beri vardı ama tek formun içinde saklıydı: şef ad, telefon ve
 *    KULLANILMAYACAK bir şifre yazıp "Ekle"ye basıyor, ancak gelen hatanın
 *    altında "bu şantiyeye de ekle" düğmesini görüyordu. Kimse o yola girmedi.
 *    Şimdi numara önce sorulur:
 *      - kayıtlı değil      → yeni hesap formu (ad, e-posta, şifre/davet, rol)
 *      - başka şantiyede    → adı gösterilir, yalnız rol sorulur, şifre YOK
 *      - bu şantiyede       → nerede olduğu söylenir (kapalıysa nasıl açılacağı)
 *
 * Yeni hesapta iki parola yolu var çünkü sahada iki gerçek var: ustanın
 * kurumsal e-postası olmayabilir (şef şifreyi koyar, telefonda söyler), ofis
 * çalışanının vardır (kendi şifresini kendi belirlesin, kimse bilmesin).
 *
 * ⚠️ Davet yolunda e-posta ZORUNLU — bağlantının gideceği yer yoksa hesap
 *    erişilemez açılırdı. Sunucu da aynı kapıyı tutuyor.
 *
 * ⚠️ Mevcut hesabı eklemek BİLİNÇLİ bir tık olmalı: sunucu `mevcutHesabaEkle`
 *    olmadan eklemez. Sessiz ekleme, bir şefin rastgele numara yazıp
 *    tanımadığı birinin hesabına kendi şantiyesine erişim vermesi demek olurdu.
 */
function YeniKullanici({ onEklendi }: { onEklendi: () => void }) {
  const [telefon, setTelefon] = useState('');
  /** İlk adımın cevabı. `null` → numara henüz sorulmadı. */
  const [sorgu, setSorgu] = useState<TelefonSorgu | null>(null);
  const [ad, setAd] = useState('');
  const [email, setEmail] = useState('');
  const [parola, setParola] = useState('');
  const [yontem, setYontem] = useState<'sifre' | 'davet'>('sifre');
  const [rol, setRol] = useState<Rol>('usta');
  const [test, setTest] = useState(false);
  const [mesgul, setMesgul] = useState(false);
  const [hata, setHata] = useState<string | null>(null);
  const [bilgi, setBilgi] = useState<string | null>(null);

  async function telefonuSor(): Promise<TelefonSorgu> {
    const s = await api.post<TelefonSorgu>('/kullanicilar/telefon-sorgula', { telefon });
    setSorgu(s);
    if (s.durum === 'bu_santiyede') {
      setHata(
        s.aktif
          ? `Bu numara bu şantiyede zaten kayıtlı: ${s.ad}.`
          : `Bu numara bu şantiyede ${s.ad} adına kayıtlı ama hesabı kapalı. Yukarıdaki "Kaldırılmış kayıtları da göster" kutusunu işaretleyip listeden yeniden açabilirsiniz.`,
      );
    }
    return s;
  }

  async function devam(e: SyntheticEvent) {
    e.preventDefault();
    setHata(null);
    setBilgi(null);
    setMesgul(true);
    try {
      await telefonuSor();
    } catch (err) {
      setHata(err instanceof Error ? err.message : 'Numara sorgulanamadı');
    } finally {
      setMesgul(false);
    }
  }

  function basaDon() {
    setSorgu(null);
    setHata(null);
  }

  async function ekle(e: SyntheticEvent) {
    e.preventDefault();
    if (!sorgu || sorgu.durum === 'bu_santiyede') return;
    setHata(null);
    setBilgi(null);
    setMesgul(true);
    const mevcut = sorgu.durum === 'baska_santiyede' ? sorgu : null;
    try {
      const c = await api.post<{
        yeniHesap: boolean;
        hesapAcildi?: boolean;
        davetGonderildi?: boolean;
        davetHatasi?: string;
      }>(
        '/kullanicilar',
        mevcut
          ? {
              // Ad hesaptan gelir; sunucu da hesaptakini yazar.
              ad: mevcut.ad,
              telefon,
              rol,
              sistemRol: 'kullanici',
              testKullanicisi: false,
              mevcutHesabaEkle: true,
            }
          : {
              ad,
              telefon,
              email: email.trim() || undefined,
              ...(yontem === 'sifre' ? { parola } : {}),
              yontem,
              rol,
              sistemRol: 'kullanici',
              testKullanicisi: test,
            },
      );
      /*
       * MEVCUT HESAP EKLENDIYSE DAVET DIYE BIR SEY YOK — kisi zaten giris
       * yapabiliyor, sifresi kendi hesabinda. "Davet gönderilemedi" demek
       * sefi olmayan bir arizanin pesine dusururdu.
       */
      if (!c.yeniHesap) {
        setBilgi(
          `${mevcut?.ad ?? ad} bu şantiyeye eklendi. Mevcut şifresiyle giriyor` +
            (c.hesapAcildi ? '; kapalı olan giriş hesabı yeniden açıldı.' : '.'),
        );
      } else if (yontem === 'davet') {
        // Davet maili kuyrukta patlarsa hesap yine açılmıştır; şef bunu bilmeli.
        setBilgi(
          c.davetGonderildi
            ? `Davet gönderildi: ${email.trim()}`
            : `Hesap açıldı ama davet gönderilemedi (${c.davetHatasi ?? 'bilinmeyen hata'}). Şifreyi elle belirleyin.`,
        );
      } else {
        setBilgi(`${ad} eklendi.`);
      }
      setTelefon('');
      setSorgu(null);
      setAd('');
      setEmail('');
      setParola('');
      onEklendi();
    } catch (err) {
      // Numara sorgudan sonra başka şantiyede açıldıysa sunucu yine söyler
      // (KODLA — metin değişse de bu dal yaşar); form doğru yola döner.
      if (err instanceof ApiHata && err.kod === 'BASKA_SANTIYEDE_KAYITLI') {
        await telefonuSor().catch(() => undefined);
        return;
      }
      setHata(err instanceof Error ? err.message : 'Eklenemedi');
    } finally {
      setMesgul(false);
    }
  }

  const gir =
    'rounded-(--radius-mod) border border-line-2 px-3 py-2 text-sm outline-none focus:border-navy';
  const anaDugme =
    'rounded-(--radius-mod) bg-navy px-4 py-2 text-sm font-medium text-white disabled:opacity-40';
  const rolSecici = (
    <select className={gir} value={rol} onChange={(e) => setRol(e.target.value as Rol)}>
      {ROLLER.map((r) => (
        <option key={r} value={r}>
          {ROL_ETIKET[r]}
        </option>
      ))}
    </select>
  );
  /** İkinci adımda numara sabit görünür; değiştirmek ilk adıma döner. */
  const numaraSatiri = (
    <p className="mb-3 text-sm text-ink-2">
      Telefon <span className="num font-medium">{telefon}</span>{' '}
      <button type="button" onClick={basaDon} className="ml-1 text-xs text-navy underline">
        değiştir
      </button>
    </p>
  );

  return (
    <div className="mb-8 rounded-(--radius-mod) border border-line bg-surface shadow-(--shadow-mod) p-4">
      <h2 className="mb-3 font-semibold text-navy">Yeni kullanıcı</h2>

      {/* 1) TELEFON — kişi sistemde var mı? */}
      {(!sorgu || sorgu.durum === 'bu_santiyede') && (
        <form onSubmit={devam}>
          <p className="mb-2 text-xs text-wait">
            Önce telefon numarası. Kişi başka bir şantiyede kayıtlıysa mevcut hesabı buraya
            eklenir, yeni hesap açılmaz.
          </p>
          <div className="flex flex-wrap gap-2">
            <input
              className={`${gir} num`}
              placeholder="0554 178 17 74"
              inputMode="numeric"
              value={telefon}
              onChange={(e) => {
                setTelefon(e.target.value);
                // Numara değiştiyse önceki cevap BAŞKA bir kişiye aitti.
                setSorgu(null);
                setHata(null);
              }}
              required
            />
            <button className={anaDugme} disabled={mesgul}>
              {mesgul ? 'Bakılıyor…' : 'Devam'}
            </button>
          </div>
        </form>
      )}

      {/* 2a) BAŞKA ŞANTİYEDE KAYITLI — yalnız rol sorulur, şifre YOK. */}
      {sorgu?.durum === 'baska_santiyede' && (
        <form onSubmit={ekle}>
          {numaraSatiri}
          <p className="mb-2 text-sm text-ink-2">
            Bu numara başka bir şantiyede <strong>{sorgu.ad}</strong> adına kayıtlı. Aynı kişiyse bu
            şantiyeye ekleyin: yeni hesap açılmaz, kişi mevcut şifresiyle girer ve sağ alttaki
            seçiciden şantiyeler arasında geçer.
          </p>
          {!sorgu.hesapAktif && (
            <p className="mb-2 text-xs text-warn">
              Giriş hesabı şu an kapalı (öteki şantiyeden çıkarılmış). Eklenince yeniden açılır.
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-ink-2">Bu şantiyedeki rolü</span>
            {rolSecici}
            <button className={anaDugme} disabled={mesgul}>
              {mesgul ? 'Ekleniyor…' : `${sorgu.ad} bu şantiyeye eklensin`}
            </button>
            <button type="button" onClick={basaDon} className="text-sm text-wait underline">
              Vazgeç
            </button>
          </div>
        </form>
      )}

      {/* 2b) YENİ HESAP */}
      {sorgu?.durum === 'yeni' && (
        <form onSubmit={ekle}>
          {numaraSatiri}
          <div className="flex flex-wrap gap-2">
            <input
              className={gir}
              placeholder="Ad soyad"
              value={ad}
              onChange={(e) => setAd(e.target.value)}
              required
            />
            <input
              className={gir}
              type="email"
              placeholder={yontem === 'davet' ? 'E-posta (zorunlu)' : 'E-posta (isteğe bağlı)'}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required={yontem === 'davet'}
            />
            {yontem === 'sifre' && (
              <input
                className={gir}
                type="password"
                placeholder="Şifre (en az 8)"
                value={parola}
                onChange={(e) => setParola(e.target.value)}
                required
                minLength={8}
              />
            )}
            {rolSecici}
            <label className="flex items-center gap-2 text-sm text-ink-2">
              <input type="checkbox" checked={test} onChange={(e) => setTest(e.target.checked)} />
              Test hesabı
            </label>
            <button className={anaDugme} disabled={mesgul}>
              {mesgul ? 'Ekleniyor…' : 'Ekle'}
            </button>
          </div>

          <div className="mt-3 flex flex-wrap gap-4 border-t border-line/70 pt-3">
            {(
              [
                ['sifre', 'Şifreyi ben belirleyeyim', 'kullanıcıya telefonda söylersiniz'],
                ['davet', 'Davet maili gitsin', 'kullanıcı kendi şifresini belirler'],
              ] as const
            ).map(([d, e, n]) => (
              <label key={d} className="flex items-center gap-2 text-sm text-ink-2">
                <input
                  type="radio"
                  name="parolaYontemi"
                  checked={yontem === d}
                  onChange={() => setYontem(d)}
                />
                {e} <span className="text-xs text-wait">({n})</span>
              </label>
            ))}
          </div>
        </form>
      )}

      {hata && <p className="mt-3 text-sm text-crit">{hata}</p>}
      {bilgi && <p className="mt-3 text-sm text-ok">{bilgi}</p>}
    </div>
  );
}

function Tablo({
  baslik,
  satirlar,
  onDegisti,
  benId,
  adminMi,
}: {
  baslik: string;
  satirlar: KullaniciSatir[];
  onDegisti: () => void;
  benId?: string;
  adminMi: boolean;
}) {
  const [duzenlenen, setDuzenlenen] = useState<string | null>(null);
  const [sifreAcilan, setSifreAcilan] = useState<string | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [bilgi, setBilgi] = useState<string | null>(null);

  if (satirlar.length === 0) return null;

  async function durum(k: KullaniciSatir) {
    try {
      await api.patch(`/kullanicilar/${k.id}/durum`, { aktif: !k.aktif });
      onDegisti();
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'İşlem başarısız');
    }
  }

  /**
   * Şifre belirleme bağlantısını yeniden gönderir. Sahada en sık ihtiyaç:
   * mail spam'e düştü, kullanıcı sildi ya da bağlantının süresi doldu.
   */
  async function davet(k: KullaniciSatir) {
    setHata(null);
    setBilgi(null);
    try {
      await api.post(`/kullanicilar/${k.id}/davet`, {});
      setBilgi(`${k.ad} için şifre belirleme bağlantısı gönderildi: ${k.email}`);
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Davet gönderilemedi');
    }
  }

  return (
    <section className="mb-8">
      <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-wait">{baslik}</h2>
      {hata && (
        <p className="mb-2 rounded-(--radius-mod) bg-crit/10 p-2 text-sm text-crit">{hata}</p>
      )}
      {bilgi && (
        <p className="mb-2 rounded-(--radius-mod) bg-ok-bg p-2 text-sm text-ok">{bilgi}</p>
      )}
      <div className="overflow-x-auto rounded-(--radius-mod) border border-line bg-surface shadow-(--shadow-mod)">
        <table className="w-full text-sm">
          <thead className="border-b border-line text-left text-wait">
            <tr>
              <th className="px-3 py-2 font-medium">Sicil</th>
              <th className="px-3 py-2 font-medium">Ad</th>
              <th className="px-3 py-2 font-medium">Telefon</th>
              <th className="px-3 py-2 font-medium">E-posta</th>
              <th className="px-3 py-2 font-medium">Rol</th>
              <th className="px-3 py-2 font-medium">Durum</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {satirlar.map((k) =>
              duzenlenen === k.id ? (
                <DuzenlemeSatiri
                  key={k.id}
                  k={k}
                  adminMi={adminMi}
                  onIptal={() => setDuzenlenen(null)}
                  onKaydedildi={() => {
                    setDuzenlenen(null);
                    onDegisti();
                  }}
                  onHata={setHata}
                />
              ) : (
                <Fragment key={k.id}>
                <tr className="border-b border-line/70 last:border-0">
                  <td className="num px-3 py-2">{k.sicilNo ?? '—'}</td>
                  <td className="px-3 py-2">
                    {k.ad}
                    {k.sistemRol === 'admin' && (
                      <span className="ml-2 rounded bg-navy px-1.5 py-0.5 text-xs text-white">
                        admin
                      </span>
                    )}
                  </td>
                  <td className="num px-3 py-2 text-wait">{k.telefon}</td>
                  <td className="px-3 py-2 text-wait">
                    {k.email ?? <span className="text-faint">—</span>}
                  </td>
                  <td className="px-3 py-2">{k.rol ? rolEtiket(k.rol) : '—'}</td>
                  <td className="px-3 py-2">
                    <span className={k.aktif ? 'text-ok' : 'text-crit'}>
                      {k.aktif ? 'Aktif' : 'Kapalı'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button
                      onClick={() => {
                        setHata(null);
                        setDuzenlenen(k.id);
                      }}
                      className="rounded-(--radius-mod) px-2 py-1 text-xs font-medium text-muted hover:bg-navy-bg hover:text-navy"
                    >
                      Düzenle
                    </button>
                    {/*
                      ŞİFRE BELİRLE her satırda var, DAVET yalnız adresi olanda.
                      Sahadaki kadronun çoğunun kurumsal e-postası yok; onların
                      tek yolu bu. Adresi olanda ikisi de durur — şef hangisinin
                      pratik olduğuna o an karar verir.
                    */}
                    <button
                      onClick={() => {
                        setHata(null);
                        setBilgi(null);
                        setSifreAcilan(sifreAcilan === k.id ? null : k.id);
                      }}
                      title="Kullanıcıya yeni bir şifre belirle"
                      className="ml-1 rounded-(--radius-mod) px-2 py-1 text-xs font-medium text-muted hover:bg-navy-bg hover:text-navy"
                    >
                      Şifre belirle
                    </button>
                    {k.email && (
                      <button
                        onClick={() => void davet(k)}
                        title="Şifre belirleme bağlantısını e-postasına yeniden gönder"
                        className="ml-1 rounded-(--radius-mod) px-2 py-1 text-xs font-medium text-muted hover:bg-navy-bg hover:text-navy"
                      >
                        Davet gönder
                      </button>
                    )}
                    {k.id !== benId && (
                      <button
                        onClick={() => void durum(k)}
                        className="ml-1 rounded-(--radius-mod) px-2 py-1 text-xs font-medium text-muted hover:bg-navy-bg hover:text-navy"
                      >
                        {k.aktif ? 'Kapat' : 'Aç'}
                      </button>
                    )}
                  </td>
                </tr>
                {sifreAcilan === k.id && (
                  <SifreSatiri
                    k={k}
                    onKapat={() => setSifreAcilan(null)}
                    onHata={setHata}
                    onBilgi={(m) => {
                      setBilgi(m);
                      setSifreAcilan(null);
                    }}
                  />
                )}
                </Fragment>
              ),
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * ŞEF/ADMİN ŞİFRE BELİRLEME — e-postası olmayan kadronun tek yolu.
 *
 * Saha kadrosunun büyük kısmının kurumsal e-postası yok; onlar "şifremi
 * unuttum" bağlantısını alamaz. Şef şifreyi buradan koyar ve kişiye
 * TELEFONDA söyler.
 *
 * ⚠️ Şifre AÇIK gösterilir — kasıtlı. Şef okuyup söyleyecek; yıldızlı bir
 *    alana yazıp "ne yazdığımı göremiyorum" durumuna düşmek, sahada şifrenin
 *    yanlış aktarılmasına ve hesabın kilitlenmesine yol açar.
 *
 * Sunucu tarafında ek kapı var: admin hesabının şifresini yalnız admin
 * sıfırlayabilir (KullaniciService.parolaSifirla).
 */
function SifreSatiri({
  k,
  onKapat,
  onHata,
  onBilgi,
}: {
  k: KullaniciSatir;
  onKapat: () => void;
  onHata: (h: string) => void;
  onBilgi: (m: string) => void;
}) {
  const [sifre, setSifre] = useState('');
  const [mesgul, setMesgul] = useState(false);

  /**
   * Okunabilir rastgele şifre — telefonda SÖYLENECEK.
   * Karışan karakterler (I/l/1, O/0) kasıtlı olarak dışarıda: sahada "büyük i
   * mi küçük L mi" tartışması, kilitlenmiş bir hesap demek.
   */
  function uret() {
    const harf = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz';
    const rakam = '23456789';
    const havuz = harf + rakam;
    const bayt = crypto.getRandomValues(new Uint8Array(10));
    const govde = [...bayt].map((b) => havuz[b % havuz.length]).join('');
    // Sonda bir rakam garantisi: "en az 8 karakter" dışında bir kural yok ama
    // tamamı harften oluşan bir şifre üretip kullanıcıyı zayıf bırakmayalım.
    setSifre(`${govde}${rakam[bayt[0]! % rakam.length]}`);
  }

  async function kaydet(e: SyntheticEvent) {
    e.preventDefault();
    setMesgul(true);
    try {
      await api.post(`/kullanicilar/${k.id}/parola-sifirla`, { yeniParola: sifre });
      onBilgi(`${k.ad} için yeni şifre: ${sifre} — kullanıcıya iletin, açık oturumları kapandı.`);
    } catch (err) {
      onHata(err instanceof Error ? err.message : 'Şifre belirlenemedi');
      setMesgul(false);
    }
  }

  return (
    <tr className="border-b border-line/70 bg-navy/5 last:border-0">
      <td colSpan={7} className="px-3 py-3">
        <form onSubmit={kaydet} className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-ink-2">
            <strong>{k.ad}</strong> için yeni şifre:
          </span>
          <input
            className="num min-w-52 rounded-(--radius-mod) border border-line-2 px-3 py-2 text-sm outline-none focus:border-navy"
            /* type=text: şef okuyup telefonda söyleyecek — bkz. bileşen yorumu */
            type="text"
            autoComplete="off"
            autoFocus
            minLength={8}
            placeholder="En az 8 karakter"
            value={sifre}
            onChange={(e) => setSifre(e.target.value)}
            required
          />
          <button
            type="button"
            onClick={uret}
            className="rounded-(--radius-mod) border border-line-2 px-3 py-2 text-xs font-medium text-muted hover:bg-surface-2 hover:text-ink"
          >
            Rastgele üret
          </button>
          <button
            disabled={mesgul || sifre.length < 8}
            className="rounded-(--radius-mod) bg-navy px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
          >
            {mesgul ? '…' : 'Şifreyi kaydet'}
          </button>
          <button
            type="button"
            onClick={onKapat}
            className="rounded-(--radius-mod) px-2 py-2 text-xs font-medium text-muted hover:bg-surface-2 hover:text-ink"
          >
            Vazgeç
          </button>
          <p className="w-full text-xs leading-relaxed text-wait">
            {k.email
              ? 'Bu kullanıcının e-postası kayıtlı — “Davet gönder” ile kendi şifresini kendisi de belirleyebilir.'
              : 'Bu kullanıcının e-posta adresi yok, “şifremi unuttum”u kullanamaz. Şifreyi kendisine iletin.'}{' '}
            Kaydedince <strong>tüm açık oturumları kapanır</strong>.
          </p>
        </form>
      </td>
    </tr>
  );
}

/** Satir ici duzenleme — ad, telefon, e-posta, rol ve (yalniz admin) sistem rolu. */
function DuzenlemeSatiri({
  k,
  adminMi,
  onIptal,
  onKaydedildi,
  onHata,
}: {
  k: KullaniciSatir;
  adminMi: boolean;
  onIptal: () => void;
  onKaydedildi: () => void;
  onHata: (h: string) => void;
}) {
  const [ad, setAd] = useState(k.ad);
  const [telefon, setTelefon] = useState(k.telefon);
  const [email, setEmail] = useState(k.email ?? '');
  const [rol, setRol] = useState<Rol>(k.rol ?? 'usta');
  const [sistemRol, setSistemRol] = useState(k.sistemRol);
  const [ekipBasi, setEkipBasi] = useState(k.ekipBasiYetkisi);
  const [kaydediliyor, setKaydediliyor] = useState(false);

  const gir = 'w-full rounded border border-line-2 px-2 py-1 text-sm outline-none focus:border-navy';

  async function kaydet() {
    setKaydediliyor(true);
    try {
      // Boş string GÖNDERİLİR — sunucu bunu "adresi sil" olarak okur.
      await api.patch(`/kullanicilar/${k.id}`, {
        ad,
        telefon,
        email: email.trim(),
        rol,
        sistemRol,
        ekipBasiYetkisi: ekipBasi,
      });
      onKaydedildi();
    } catch (e) {
      onHata(e instanceof Error ? e.message : 'Kaydedilemedi');
      setKaydediliyor(false);
    }
  }

  return (
    <tr className="border-b border-line/70 bg-navy/5 last:border-0">
      <td className="num px-3 py-2 text-wait">{k.sicilNo ?? '—'}</td>
      <td className="px-3 py-2">
        <input className={gir} value={ad} onChange={(e) => setAd(e.target.value)} autoFocus />
      </td>
      <td className="px-3 py-2">
        <input
          className={`${gir} num`}
          inputMode="numeric"
          value={telefon}
          onChange={(e) => setTelefon(e.target.value)}
        />
      </td>
      <td className="px-3 py-2">
        <input
          className={gir}
          type="email"
          placeholder="ornek@firma.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </td>
      <td className="px-3 py-2">
        <select className={gir} value={rol} onChange={(e) => setRol(e.target.value as Rol)}>
          {ROLLER.map((r) => (
            <option key={r} value={r}>
              {ROL_ETIKET[r]}
            </option>
          ))}
        </select>
        {/* Ekip başılığı ROL DEĞİL, kişi bazlı yetki — müşteri kararı (S1). */}
        <label className="mt-1 flex items-center gap-1.5 text-xs text-wait">
          <input
            type="checkbox"
            checked={ekipBasi}
            onChange={(e) => setEkipBasi(e.target.checked)}
          />
          Ekip başı yetkisi
        </label>
        {adminMi && (
          <label className="mt-1 flex items-center gap-1.5 text-xs text-wait">
            <input
              type="checkbox"
              checked={sistemRol === 'admin'}
              onChange={(e) => setSistemRol(e.target.checked ? 'admin' : 'kullanici')}
            />
            Süper admin
          </label>
        )}
      </td>
      <td className="px-3 py-2 text-wait">—</td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        <button
          onClick={() => void kaydet()}
          disabled={kaydediliyor || ad.trim().length < 2}
          className="rounded bg-navy px-2.5 py-1 text-xs text-white disabled:opacity-40"
        >
          {kaydediliyor ? '…' : 'Kaydet'}
        </button>
        <button onClick={onIptal} className="ml-1 rounded-(--radius-mod) px-2 py-1 text-xs font-medium text-muted hover:bg-surface-2 hover:text-ink">
          Vazgeç
        </button>
      </td>
    </tr>
  );
}

/**
 * MAİL KİLİDİ — müşteri adreslerine kazara gönderimi engeller.
 *
 * ⚠️ Sistem canlıya alınmadan önce test edilirken gerçek Nuh Çimento adresleri
 *    sistemde duruyor. Bir teslim tiki, bir günlük rapor denemesi ya da
 *    zamanlanmış bir yeniden deneme müşteriye "iskele kuruldu" maili
 *    gönderebilir. O mail geri alınamaz ve müşteride "sistem canlı" izlenimi
 *    yaratır.
 *
 * Kilit VARSAYILAN OLARAK AÇIK ve yalnız admin kapatabilir. Kapatmak, gerçek
 * mail gitmesi demek — geri alınamaz bir eylem, bu yüzden onay soruluyor.
 */
interface MailKilidiDurumu {
  /** YÜRÜRLÜKTEKİ kilit — ham ayar değil, müşteri kilitleriyle birleşmiş hâli. */
  aktif: boolean;
  domainler: string[];
  /** Küresel anahtarın kendisi; müşteri kilitlerinden bağımsız. */
  ayarAktif: boolean;
  ayarDomainler: string[];
  musteriler: { kod: string; kilitli: boolean; alanAdlari: string[] }[];
  bekleyen: number;
}

function MailKilidiAyari() {
  const [kilit, setKilit] = useState<MailKilidiDurumu | null>(null);
  const [onay, setOnay] = useState(false);
  /** Kilidi kaldırılmak üzere onay bekleyen müşteri kodu — aynı anda yalnız biri. */
  const [musteriOnay, setMusteriOnay] = useState<string | null>(null);
  const [durum, setDurum] = useState<string | null>(null);

  const yukle = () =>
    api
      .get<MailKilidiDurumu>('/ayarlar/mail-kilidi')
      .then(setKilit)
      .catch(() => setDurum('Mail kilidi okunamadı.'));

  useEffect(() => {
    void yukle();
  }, []);

  if (!kilit) return null;

  async function yaz(aktif: boolean, domainler: string[]) {
    setDurum('Kaydediliyor…');
    try {
      const c = await api.patch<{ ok: boolean; hata?: string }>('/ayarlar/mail-kilidi', {
        aktif,
        domainler,
      });
      setDurum(c.ok ? (aktif ? 'Kilit AÇIK ✓' : 'Kilit kapatıldı — mailler gidecek') : (c.hata ?? 'Olmadı'));
      setOnay(false);
      await yukle();
    } catch (e) {
      setDurum(e instanceof Error ? e.message : 'Kaydedilemedi');
    }
  }

  async function musteriYaz(kod: string, kilitli: boolean) {
    setDurum('Kaydediliyor…');
    try {
      const c = await api.patch<{ ok: boolean; hata?: string; sokumBildirimi?: number }>(
        '/ayarlar/mail-kilidi/musteri',
        { kod, kilitli },
      );
      setDurum(
        c.ok
          ? kilitli
            ? `${kod} kilitlendi ✓`
            : `${kod} kilidi kaldırıldı — bu müşteriye gerçek mail gidecek` +
              (c.sokumBildirimi ? ` · son 48 saatin ${c.sokumBildirimi} söküm bildirimi gönderildi` : '')
          : (c.hata ?? 'Olmadı'),
      );
      setMusteriOnay(null);
      await yukle();
    } catch (e) {
      setDurum(e instanceof Error ? e.message : 'Kaydedilemedi');
    }
  }

  const acik = kilit.aktif;

  return (
    <section
      className={`mt-10 rounded-(--radius-mod) border p-5 shadow-(--shadow-mod) ${
        acik ? 'border-warn-line bg-warn-bg' : 'border-crit/40 bg-crit/5'
      }`}
    >
      <h2 className={`mb-1 font-semibold ${acik ? 'text-warn' : 'text-crit'}`}>
        Mail kilidi — {acik ? 'AÇIK (müşteriye mail gitmiyor)' : 'KAPALI (gerçek mail gidiyor)'}
        {acik && !kilit.ayarAktif && (
          <span className="ml-1 font-normal text-wait">
            · küresel anahtar kapalı, müşteri kilitleri tutuyor
          </span>
        )}
      </h2>
      <p className="mb-3 text-sm leading-relaxed text-wait">
        Kilit açıkken aşağıdaki alan adlarına giden <strong>hiçbir mail gönderilmez</strong> —
        teslim maili, günlük rapor, davet, şifre sıfırlama. Engellenen mail silinmez,
        kuyrukta <em>kilitli</em> olarak durur; kilidi açtığınızda neyin gitmediği görülür.
      </p>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="text-xs text-wait">Engellenen alan adları:</span>
        {kilit.domainler.length ? (
          kilit.domainler.map((d) => (
            <span key={d} className="rozet num border-line-2 bg-surface text-xs text-ink-2">
              {d}
            </span>
          ))
        ) : (
          <span className="text-xs text-crit">liste boş — hiçbir şey engellenmiyor</span>
        )}
      </div>

      {/*
       * ⚠️ MÜŞTERİ KIRILIMI, KÜRESEL ANAHTARDAN AYRI. Aşağıdaki anahtarı
       *    kapatmak (pilota geçiş) tek başına buradaki müşterileri AÇMAZ;
       *    her müşteri için ayrı karar gerekir. Kilit tek anahtarken, Nuh
       *    pilotu için verilen karar Akçansa'ya da mail gönderirdi.
       */}
      <div className="mb-3 rounded-(--radius-mod) bg-surface px-3 py-2">
        <div className="mb-1.5 text-xs text-wait">Müşteri bazlı kilit</div>
        <div className="space-y-1.5">
          {kilit.musteriler.map((m) => (
            <div key={m.kod} className="flex flex-wrap items-center gap-2 text-sm">
              <strong className="w-20 shrink-0 text-ink">{m.kod}</strong>
              <span className={m.kilitli ? 'text-ok' : 'text-crit'}>
                {m.kilitli ? 'kilitli — mail gitmez' : 'AÇIK — mail gider'}
              </span>
              {m.alanAdlari.length > 0 && (
                <span className="text-faint">· {m.alanAdlari.join(', ')}</span>
              )}
              {/*
                ⚠️ HER MÜŞTERİ AYRI KARAR (07.09.2026). Model 21.08'den beri
                   müşteri bazlıydı ama YAZAN uç nokta yoktu; panelden yalnız
                   küresel anahtar değiştirilebildiği için "Nuh'a mail gitsin,
                   Akçansa'ya gitmesin" fiilen imkânsızdı.

                   Açma tıkı ONAY İSTER, kilitleme istemez: kilitlemek geri
                   alınabilir bir eylem, açmak değil.
              */}
              {m.kilitli ? (
                musteriOnay === m.kod ? (
                  <span className="flex items-center gap-2">
                    <button
                      onClick={() => void musteriYaz(m.kod, false)}
                      className="rounded-(--radius-mod) bg-crit px-2.5 py-1 text-xs font-medium text-white"
                    >
                      Evet, {m.kod} kilidini kaldır
                    </button>
                    <button
                      onClick={() => setMusteriOnay(null)}
                      className="text-xs text-muted underline hover:text-ink"
                    >
                      vazgeç
                    </button>
                  </span>
                ) : (
                  <button
                    onClick={() => setMusteriOnay(m.kod)}
                    className="text-xs text-muted underline underline-offset-2 hover:text-crit"
                  >
                    kilidi kaldır
                  </button>
                )
              ) : (
                <button
                  onClick={() => void musteriYaz(m.kod, true)}
                  className="rounded-(--radius-mod) border border-line-2 px-2.5 py-1 text-xs text-navy hover:bg-navy-bg"
                >
                  kilidi tak
                </button>
              )}
            </div>
          ))}
        </div>
        <p className="mt-1.5 text-xs leading-relaxed text-muted">
          Bu kilitler aşağıdaki anahtardan bağımsızdır: anahtar kapatılsa bile
          burada <em>kilitli</em> görünen müşteriye mail gitmez. Bir müşterinin
          kilidini kaldırmak <strong>yalnız o müşteriyi</strong> açar.
        </p>
      </div>

      {kilit.bekleyen > 0 && (
        <p className="mb-3 rounded-(--radius-mod) bg-surface px-3 py-2 text-sm text-ink-2">
          Kilit yüzünden gönderilmemiş <strong className="num">{kilit.bekleyen}</strong> mail
          var. Kilit açılınca bunlar <strong>gönderilmez</strong>; yalnız son 48 saatte
          kapanan sökümlerin bildirimi gider.
        </p>
      )}

      {acik ? (
        !onay ? (
          <button
            onClick={() => setOnay(true)}
            className="text-sm text-muted underline underline-offset-2 hover:text-crit"
          >
            Kilidi kaldır — müşteriye gerçek mail gitmeye başlasın
          </button>
        ) : (
          <div className="rounded-(--radius-mod) border border-crit/40 bg-surface p-3">
            <p className="mb-2 text-sm text-crit">
              <strong>Emin misiniz?</strong> Küresel kilit kalkar ve yukarıda{' '}
              <em>kilitli</em> görünmeyen müşterilere gerçek mail gitmeye başlar.{' '}
              <strong>Gönderilen mail geri alınamaz.</strong> Müşteri bazlı kilitler
              bundan etkilenmez; onları açmak ayrı bir karardır.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => void yaz(false, kilit.ayarDomainler)}
                className="rounded-(--radius-mod) bg-crit px-4 py-2 text-sm font-medium text-white"
              >
                Evet, kilidi kaldır
              </button>
              <button
                onClick={() => setOnay(false)}
                className="rounded-(--radius-mod) px-3 py-2 text-sm text-muted hover:text-ink"
              >
                Vazgeç
              </button>
            </div>
          </div>
        )
      ) : (
        <button
          onClick={() => void yaz(true, kilit.ayarDomainler.length ? kilit.ayarDomainler : ['nuhcimento.com.tr'])}
          className="rounded-(--radius-mod) bg-navy px-4 py-2 text-sm font-medium text-white"
        >
          Kilidi tekrar tak
        </button>
      )}

      {durum && <p className="mt-3 text-sm text-wait">{durum}</p>}
    </section>
  );
}

/**
 * DİKKAT EŞİKLERİ — kural seti belgesinin "Kalibrasyon" bölümü.
 *
 * Belge bu süreleri açıkça **taslak** ilan ediyor: *"Aşağıdaki süreler taslaktır;
 * sahadaki gerçeğe göre birlikte netleştirelim."* Bu yüzden koda gömülmezler.
 * Bedirhan'la sahada oturup canlı ayarlanabilmeli — yeni deploy beklemeden.
 *
 * ⚠️ Eşiği büyütmek kartı geciktirir, küçültmek panoyu gürültüye boğar.
 *    İkisi de "bugün neye bakmalıyım" ekranını işlevsizleştirir.
 */
const ESIK_ALANLARI = [
  { k: 'kontrol_onay_bekliyor', e: 'Kontrol & onay bekliyor', not: 'ekip "kurdum" dedi, şef onaylamadı' },
  { k: 'kesif_bekliyor', e: 'Keşif bekliyor', not: 'atandı, keşif yapılmadı' },
  { k: 'malzeme_bekliyor', e: 'Malzeme bekliyor', not: 'keşif bitti, sevk yok' },
  { k: 'kurulum_bekliyor', e: 'Kurulum bekliyor', not: 'izin+malzeme hazır, başlanmadı' },
  { k: 'sokum_revize_bekliyor', e: 'Söküm / revizyon bekliyor', not: 'emir verildi, başlanmadı' },
  { k: 'delegasyon_bekliyor', e: 'Delegasyon bekleyen iş', not: 'keşifçi atanmadı' },
] as const;

type Esikler = Record<string, [number, number | null] | number>;

function DikkatEsikAyari() {
  const [esikler, setEsikler] = useState<Esikler | null>(null);
  const [durum, setDurum] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ esikler: Esikler }>('/ayarlar/dikkat-esikleri')
      .then((d) => setEsikler(d.esikler))
      .catch(() => setDurum('Eşikler yüklenemedi.'));
  }, []);

  if (!esikler) return null;

  const cift = (k: string) => esikler[k] as [number, number | null];
  const sayi = (k: string) => esikler[k] as number;

  const yaz = (k: string, v: [number, number | null] | number) =>
    setEsikler({ ...esikler, [k]: v });

  async function kaydet() {
    setDurum('Kaydediliyor…');
    try {
      await api.patch('/ayarlar/dikkat-esikleri', esikler);
      setDurum('Kaydedildi ✓');
    } catch (e) {
      setDurum(e instanceof Error ? e.message : 'Kaydedilemedi');
    }
  }

  const gir = 'num w-20 rounded border border-line-2 px-2 py-1 text-sm outline-none focus:border-navy';

  return (
    <section className="mt-10 rounded-(--radius-mod) border border-line bg-surface shadow-(--shadow-mod) p-5">
      <h2 className="mb-1 font-semibold text-navy">Ayarlar — dikkat eşikleri</h2>
      <p className="mb-4 text-sm text-wait">
        Bir iş kaç <strong>saat</strong> beklerse panoda uyarı, kaç saatte kritik olur? Kural seti
        belgesi bu süreleri <em>taslak</em> ilan etti — sahadaki gerçeğe göre burada ayarlanır.
      </p>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-line text-left text-wait">
            <tr>
              <th className="py-2 pr-3 font-medium">Tetikleyici</th>
              <th className="px-3 py-2 font-medium">Uyarı (saat)</th>
              <th className="px-3 py-2 font-medium">Kritik (saat)</th>
            </tr>
          </thead>
          <tbody>
            {ESIK_ALANLARI.map(({ k, e, not }) => {
              const [u, kr] = cift(k);
              return (
                <tr key={k} className="border-b border-line/70 last:border-0">
                  <td className="py-2 pr-3">
                    {e}
                    <span className="block text-xs text-wait">{not}</span>
                  </td>
                  <td className="px-3 py-2">
                    <input
                      className={gir}
                      type="number"
                      min={0}
                      value={u}
                      onChange={(ev) => yaz(k, [Number(ev.target.value), kr])}
                    />
                  </td>
                  <td className="px-3 py-2">
                    {/* null = bu tetikleyici kritiğe TERFİ ETMEZ (ör. kurulum bekliyor). */}
                    <input
                      className={gir}
                      type="number"
                      min={0}
                      placeholder="terfi yok"
                      value={kr ?? ''}
                      onChange={(ev) =>
                        yaz(k, [u, ev.target.value === '' ? null : Number(ev.target.value)])
                      }
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex flex-wrap gap-6 border-t border-line/70 pt-4 text-sm">
        <label className="flex items-center gap-2">
          Günlük rapor uyarı saati
          <input
            className={gir}
            type="number"
            min={0}
            max={23}
            value={sayi('gunluk_rapor_saati')}
            onChange={(e) => yaz('gunluk_rapor_saati', Number(e.target.value))}
          />
        </label>
        <label className="flex items-center gap-2">
          İzin override — kaç adette uyarı
          <input
            className={gir}
            type="number"
            min={1}
            value={sayi('izin_override_adet')}
            onChange={(e) => yaz('izin_override_adet', Number(e.target.value))}
          />
        </label>
        <label className="flex items-center gap-2">
          Erteleme süresi (saat)
          <input
            className={gir}
            type="number"
            min={1}
            value={sayi('erteleme_saat')}
            onChange={(e) => yaz('erteleme_saat', Number(e.target.value))}
          />
        </label>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button
          onClick={() => void kaydet()}
          className="rounded-(--radius-mod) bg-navy px-4 py-2 text-sm font-medium text-white"
        >
          Eşikleri kaydet
        </button>
        {durum && <span className="text-sm text-wait">{durum}</span>}
      </div>

      <p className="mt-4 border-t border-line/70 pt-3 text-xs leading-relaxed text-wait">
        Güvenlik döngüsü ve kapanış bloğu <strong>eşiksizdir</strong> — anında kritik olur,
        ertelenemez ve buradan yumuşatılamaz.
      </p>
    </section>
  );
}
